import { useEffect, useMemo, useState } from "react";
import { format, subDays } from "date-fns";
import { MapPin, Camera, CircleNotch } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

const istTime = (ts) =>
  ts ? new Date(ts).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false }) : "—";

function PhotoDialog({ visit, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!visit) return;
    setLoading(true);
    setError(false);
    setData(null);
    api.get(`/visit-photo/${visit.id}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [visit]);

  return (
    <Dialog open={!!visit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="visit-photo-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {visit?.shop_name} <span className="text-slate-400 font-normal text-sm">· {visit?.employee_name}</span>
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 font-mono-data -mt-2">
          {visit?.date} · {istTime(visit?.ts)} IST{visit?.remarks ? ` · ${visit.remarks}` : ""}
        </p>
        {loading && (
          <div className="flex items-center justify-center py-16 text-slate-400" data-testid="visit-photo-loading">
            <CircleNotch size={28} className="animate-spin" />
          </div>
        )}
        {!loading && data?.photo && (
          <img src={data.photo} alt={`Visit at ${visit?.shop_name}`} className="w-full rounded-md border border-slate-200 max-h-[65vh] object-contain bg-slate-950" data-testid="visit-photo-img" />
        )}
        {!loading && !error && data && !data.photo && (
          <p className="text-sm text-slate-500 py-8 text-center" data-testid="visit-photo-none">
            No photo was captured for this visit.
          </p>
        )}
        {!loading && error && (
          <p className="text-sm text-red-500 py-8 text-center" data-testid="visit-photo-error">
            Could not load the photo — try again shortly.
          </p>
        )}
        {visit && (
          <a
            href={`https://www.google.com/maps?q=${visit.latitude},${visit.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-[#007AFF] hover:underline font-mono-data"
          >
            <MapPin size={12} /> View visit location on map
          </a>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Visits() {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [to, setTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [employee, setEmployee] = useState("all");
  const [photoVisit, setPhotoVisit] = useState(null);

  useEffect(() => {
    api.get("/proxy/visits/all").then((r) => setVisits(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const names = useMemo(() => [...new Set(visits.map((v) => v.employee_name))].sort(), [visits]);
  const filtered = visits.filter(
    (v) => v.date >= from && v.date <= to && (employee === "all" || v.employee_name === employee)
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="visits-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Field Activity</p>
          <h1 className="font-heading font-black tracking-tight text-3xl">Field Visits ({filtered.length})</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="visits-employee-filter" value={employee} onChange={(e) => setEmployee(e.target.value)} className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007AFF]">
            <option value="all">All employees</option>
            {names.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <input type="date" data-testid="visits-from-input" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-slate-300 rounded-md px-3 py-2 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-[#007AFF]" />
          <span className="text-slate-400 text-sm">→</span>
          <input type="date" data-testid="visits-to-input" value={to} onChange={(e) => setTo(e.target.value)} className="border border-slate-300 rounded-md px-3 py-2 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-[#007AFF]" />
        </div>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden max-w-full">
        <table className="w-full text-sm" data-testid="visits-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left">
              {["Date", "Time", "Employee", "Shop", "Customer", "Location", "Photo", "Remarks"].map((h) => (
                <th key={h} className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((v) => (
              <tr key={v.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3 font-mono-data text-xs">{v.date}</td>
                <td className="px-4 py-3 font-mono-data text-xs">{istTime(v.ts)}</td>
                <td className="px-4 py-3 font-medium">{v.employee_name}</td>
                <td className="px-4 py-3">{v.shop_name}</td>
                <td className="px-4 py-3 text-slate-600">{v.customer_name || "—"}</td>
                <td className="px-4 py-3">
                  <a
                    href={`https://www.google.com/maps?q=${v.latitude},${v.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-[#007AFF] hover:underline font-mono-data"
                  >
                    <MapPin size={12} /> {v.latitude?.toFixed(4)}, {v.longitude?.toFixed(4)}
                  </a>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => setPhotoVisit(v)}
                    data-testid={`visit-photo-btn-${v.id}`}
                    className="flex items-center gap-1 text-xs text-[#007AFF] hover:underline"
                  >
                    <Camera size={14} /> View
                  </button>
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs max-w-[200px] truncate">{v.remarks || "—"}</td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No visits in this range.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <PhotoDialog visit={photoVisit} onClose={() => setPhotoVisit(null)} />
    </div>
  );
}
